// Logbook + match chat, extracted from GameBoard.jsx. logAction is the
// actor-side entry point; logSystem and addAdvisoryLog are system lines.
// Mention hover/click wire logbook card references back to the inspector.
//
// A log line is a MESSAGE, not a sentence: `{ key, values }`, where `key` is
// the English template ("{name} taps {card}") and `values` fills its
// placeholders. Every reader renders it through t() at render time, so each
// player sees the log in their own language and a language switch re-renders
// the whole log. A value may itself be a message (`{ key: 'A unit' }`, a
// redacted card label) and is translated with the line. The wire carries the
// message plus its English rendering, so an older client still reads it.
// Chat is the one exception: a player's own words are stored as `text` and
// never translated.

import { rectToCanvasRect } from '../../../utils/cardMentions';
import { EM_DASH } from '../../../lib/typography';
import { t, k } from '../../../localization/t';
import { msg, fill } from '../../../utils/game/logMessage';

// Normalize the (text | message, values) input every log entry point accepts.
export function logMessage(input, values) {
  if (input && typeof input === 'object') return input;
  return values ? { key: String(input), values } : { key: String(input) };
}

// The line as the current language renders it. Chat stays verbatim.
export function logEntryText(entry) {
  if (!entry) return '';
  if (entry.kind === 'chat' || typeof entry.key !== 'string') return entry.text || '';
  return t(entry.key, entry.values);
}

// The English rendering that rides the wire next to the message.
export function logWireText(message) {
  return fill(message.key, message.values);
}

// The one turn-banner format. Authored from the turn-flip fold under auth
// (socketListeners.logTurnBoundaryFromFold) and locally on the legacy pass
// path (turnPhaseRunner.completePassTurn); both must render identically.
export function turnBanner(name, turnNo) {
  return { key: k("{dash} {name}'s Turn {n} {dash}"), values: { dash: EM_DASH, name, n: turnNo } };
}

// Close all floating menus (burger, dice, ambience, context, ring).
// Called on canvas click and when opening any menu so only one is
// visible at a time.
// ── Game action log ────────────────────────────────────────────
// v1.104. Convenience for the "the engine thinks this is illegal but
// we let it through anyway" advisory line. Renders yellow in the log.
// Use whenever a play / move / activation would have been blocked
// before the relax-gating change. The action proceeds; the log entry
// is the player's hint that the engine wasn't sure.
// Rules-engine advisory ("the engine thinks this is illegal but we let
// it through"). Renders yellow on both sides. A manual session emits none
// Nothing is applying the rules, so there is no engine opinion to report.
// Pass the subject `card` whenever the advisory is about a specific card:
// the engine's message embeds the real card name, so while the opponent
// has not yet seen that card (no `nameKnown`) the line renders locally but
// never crosses the wire. A broadcast would reveal the hidden identity.
export function addAdvisoryLog(host, text, card = null, values) {
  if (!host._automationsEnabled()) return;
  const message = { key: k('! {message}'), values: { message: logMessage(text, values) } };
  if (card && !card.nameKnown) {
    host.addLogEntry(message, 'system', 'warning');
    return;
  }
  host.logSystem(message, 'warning');
}

// Log-safe display label for a card. The opponent may be told a card's
// name ONLY if its face has been shown to them at least once since it
// entered play. Played/flipped face up, cast, or explicitly revealed.
// We track that with the sticky `card.nameKnown` flag (set at every reveal
// point; once true it never clears). Until then the name is concealed.
//
// `faceDown` is deliberately NOT the predicate: a card can be hidden from
// the opponent without being face down (in hand, in the spellbook/atlas,
// or mid-move between two hidden zones. The recurring leak), and a card
// that was once public stays nameable even after it is flipped back down.
// The logbook is permanent and crosses the wire, so a single bad line
// leaks a hidden identity forever. When in doubt this returns the neutral
// fallback (a missed reveal point only over-anonymizes; it never leaks).
//
// The result is a log VALUE: a name string, or the fallback message (a
// translatable `msg('a card')`), so always pass it in a message's values,
// never into a template literal.
export function cardLogLabel(card, fallback = msg('a card')) {
  if (!card?.nameKnown) return fallback;
  return card.name || fallback;
}

// Funnel for any logbook event that should appear in BOTH players'
// logbooks. Appends locally as 'you' and broadcasts the entry text on
// its own channel; the opponent's `logbook:entry` handler appends with
// actor='opponent'. Use this instead of addLogEntry for every
// multiplayer-visible action. Submerges, taps, plays, level changes,
// etc. Truly local-only entries (sandbox feedback, dev-only diagnostics)
// stay on addLogEntry directly; rule-driven system events and rules
// warnings go through logSystem / logWarning so they too propagate.
// `logAction(host, key, values, kind)`; the legacy `(host, text, kind)` form
// still works while call sites migrate, the text simply has no placeholders.
export function logAction(host, text, values, kind = 'action') {
  if (typeof values === 'string') { kind = values; values = undefined; }
  if (!text) return;
  const message = logMessage(text, values);
  host.addLogEntry(message, 'you', kind);
  host.sync?.logbookEntry?.({ ...message, text: logWireText(message) }, kind);
}

// Multiplayer-visible game-rule announcement (turn banner, Death's Door,
// GAME OVER, first-player roll result, rules-engine warnings, …). Renders
// centered without a player-name prefix on both clients. Use whenever the
// event is deterministic / actor-driven but doesn't belong to a single
// player's voice.
export function logSystem(host, text, values, kind = 'system') {
  if (typeof values === 'string') { kind = values; values = undefined; }
  if (!text) return;
  const message = logMessage(text, values);
  host.addLogEntry(message, 'system', kind);
  host.sync?.logbookEntry?.({ ...message, text: logWireText(message) }, kind, { system: true });
}

// Tabs that put chat messages on screen. 'both' interleaves the whole
// gameLog chronologically, so a message arriving while it is open has
// already been seen. Used by the unread badge, the composer and the
// focus-chat hotkey so all three agree on what "looking at chat" means.
export function isChatVisibleTab(tab) {
  return tab === 'chat' || tab === 'both';
}

// Entries the logbook body renders for a tab. Chat and actions share one
// gameLog array (distinguished by `kind`), so 'both' is simply no filter.
export function logbookEntriesFor(tab, gameLog) {
  const entries = gameLog || [];
  if (tab === 'both') return entries;
  if (tab === 'chat') return entries.filter((e) => e.kind === 'chat');
  return entries.filter((e) => e.kind !== 'chat');
}

export function addLogEntry(host, text, actor = 'opponent', kind = 'action') {
  const message = kind === 'chat' && typeof text === 'string' ? { text } : logMessage(text);
  const scroll = host.logbookScrollRef;
  // If the user is already at (or within a few pixels of) the bottom,
  // re-pin to the bottom after the new entry renders. Otherwise leave
  // their scroll position alone so they can read older entries.
  const wasPinnedToBottom = scroll
    ? scroll.scrollHeight - scroll.scrollTop - scroll.clientHeight < 8
    : true;
  // Badge the Chat tab for an incoming message the user isn't currently
  // looking at (no chat-showing tab active, or the whole logbook collapsed).
  const isIncomingChat = kind === 'chat' && actor !== 'you';
  host.setState((s) => ({
    gameLog: [...s.gameLog, { id: Date.now() + Math.random(), ...message, actor, kind, time: new Date() }],
    chatUnread: s.chatUnread || (isIncomingChat && (!isChatVisibleTab(s.logbookTab) || !s.showGameLog)),
  }), () => {
    if (wasPinnedToBottom && host.logbookScrollRef) {
      host.logbookScrollRef.scrollTop = host.logbookScrollRef.scrollHeight;
    }
    host.updateLogbookThumb();
  });
}

export function setLogbookScrollRef(host, el) {
  if (el && el !== host.logbookScrollRef) {
    // First attachment. Start at the bottom so the latest entries
    // are visible without forcing the user there on every re-render.
    el.scrollTop = el.scrollHeight;
  }
  host.logbookScrollRef = el;
  host.updateLogbookThumb();
}

// Open the card inspector for a @-mention click in a logbook chat
// entry. Synthesizes a minimal card instance. No board-state fields
// (statuses, equippedCards, face). Since the player is just looking
// up a card someone referenced, not inspecting a table card.
export function handleLogbookMentionHover(host, cardId, element) {
  const full = host._getFullCard(cardId);
  if (!full || !element?.getBoundingClientRect) return;
  const rect = rectToCanvasRect(element.getBoundingClientRect());
  host.setState({ logbookMentionPreview: { card: full, rect } });
  const printing = full.printings?.[0];
  if (printing?.image_url) {
    host._setFocusedPreviewCard({
      cardId: full.unique_id,
      imageUrl: printing.image_url,
      name: full.name,
      foiling: printing.foiling || null,
    });
  }
}

export function handleLogbookMentionHoverEnd(host) {
  if (host.state.logbookMentionPreview) {
    host.setState({ logbookMentionPreview: null });
  }
}

// Replay feed row hover: highlight the cell of every in-play card whose
// catalog id the row mentions (all copies, if several are out). `null` /
// empty clears. Only re-renders when the key set actually changes, since
// rows fire this on every enter/leave.
export function setMentionCellHighlights(host, cardIds) {
  const keys = [];
  if (Array.isArray(cardIds) && cardIds.length) {
    const wanted = new Set(cardIds);
    for (const mesh of host.meshes.values()) {
      const card = mesh?.userData?.cardInstance;
      const cell = card?.cell;
      if (!card || !wanted.has(card.cardId) || typeof cell?.col !== 'number' || typeof cell?.row !== 'number') continue;
      const key = `${cell.col},${cell.row}`;
      if (!keys.includes(key)) keys.push(key);
    }
  }
  const prev = host.state.mentionHighlightCells || [];
  if (prev.length === keys.length && prev.every((k, i) => k === keys[i])) return;
  host.setState({ mentionHighlightCells: keys });
}

export function handleOpenLogbookMention(host, cardId) {
  const sorceryCards = host.props.sorceryCards;
  const full = sorceryCards?.find((c) => c.unique_id === cardId);
  if (!full) return;
  const printing = full.printings?.[0];
  host.setState({
    inspectedCard: {
      cardId,
      imageUrl: printing?.image_url || null,
      foiling: printing?.foiling || null,
    },
    inspectorStack: [],
  });
  host.scene?.setPaused(true);
}

export function sendChatMessage(host) {
  if (host.props.isSpectating) return;
  const text = (host.state.chatInputValue || '').trim();
  if (!text) return;
  // Cap length client-side; the server broadcasts as-is so an
  // unbounded paste could blow up the opponent's logbook.
  const clipped = text.slice(0, 240);
  host.addLogEntry({ text: clipped }, 'you', 'chat');
  host._broadcastChat(clipped);
  host.setState({ chatInputValue: '' });
  // Persist our own log promptly (debounced) so the per-seat snapshot
  // carries this message before the next periodic autosave. A crash
  // right after sending must not lose it on rejoin.
  host.scheduleLocalSave();
}

/**
 * Custom scrollbar thumb for the logbook. Reads the real scroll
 * state from the hidden native scrollbar and positions an absolutely-
 * positioned indicator on top of the content. The thumb is purely
 * visual (not draggable). Users still scroll via wheel/touchpad/
 * keyboard. The native scrollbar is hidden so no gutter eats into
 * the content's right-side padding.
 */
export function updateLogbookThumb(host) {
  const scroll = host.logbookScrollRef;
  const thumb = host.logbookThumbRef;
  if (!scroll || !thumb) return;
  const { scrollTop, scrollHeight, clientHeight } = scroll;
  if (scrollHeight <= clientHeight + 1) {
    // Nothing to scroll. Hide the thumb.
    thumb.style.opacity = '0';
    thumb.style.height = '0px';
    return;
  }
  const trackHeight = clientHeight - 4; // 2px top/bottom inset
  const minThumb = 18;
  const thumbHeight = Math.max(minThumb, Math.round((clientHeight / scrollHeight) * trackHeight));
  const maxTop = trackHeight - thumbHeight;
  const progress = scrollTop / (scrollHeight - clientHeight);
  const top = 2 + Math.round(progress * maxTop);
  thumb.style.height = `${thumbHeight}px`;
  thumb.style.top = `${top}px`;
  thumb.style.opacity = '1';
}
